/**
 * Geometry for the price chart, shared by the on-screen SVG and the exported share image so the
 * two always draw the same picture. Prices plot on implied chance (the 0–1 price), which is
 * linear; the axis LABELS are in whatever odds format the viewer picked.
 */
import type { SeriesPoint, SideSeries } from "./history";

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface Plot {
  x: (t: number) => number;
  y: (price: number) => number;
  t0: number;
  t1: number;
  p0: number;
  p1: number;
  yTicks: number[];
  xTicks: number[];
}

const Y_STEPS = [0.01, 0.02, 0.025, 0.05, 0.1, 0.2];

/** Scale every series into `box`, with a little headroom and 3–5 round horizontal gridlines. */
export function plotScale(series: SideSeries[], box: Box): Plot | null {
  const pts = series.flatMap((s) => s.points);
  if (pts.length < 2) return null;
  let t0 = Infinity;
  let t1 = -Infinity;
  let lo = Infinity;
  let hi = -Infinity;
  for (const p of pts) {
    t0 = Math.min(t0, p.t);
    t1 = Math.max(t1, p.t);
    lo = Math.min(lo, p.price);
    hi = Math.max(hi, p.price);
  }
  if (t1 === t0) t1 = t0 + 1;
  const pad = Math.max((hi - lo) * 0.12, 0.01);
  const step = Y_STEPS.find((s) => (hi - lo + 2 * pad) / s <= 5) ?? 0.25;
  // Stay strictly inside (0, 1): 0% and 100% have no odds to label.
  const p0 = Math.max(step, Math.floor((lo - pad) / step) * step);
  const p1 = Math.min(1 - step, Math.ceil((hi + pad) / step) * step);

  const yTicks: number[] = [];
  for (let v = p0; v <= p1 + 1e-9; v += step) yTicks.push(Math.round(v * 1000) / 1000);
  const xTicks = [0, 1 / 3, 2 / 3, 1].map((f) => t0 + (t1 - t0) * f);

  return {
    x: (t) => box.x + ((t - t0) / (t1 - t0)) * box.w,
    y: (price) => box.y + box.h - ((price - p0) / (p1 - p0 || 1)) * box.h,
    t0,
    t1,
    p0,
    p1,
    yTicks,
    xTicks,
  };
}

/** A step line: a price holds until the next point, which is how a quote actually behaves. */
export function stepPath(points: SeriesPoint[], plot: Plot): string {
  if (!points.length) return "";
  let d = `M${plot.x(points[0].t).toFixed(1)},${plot.y(points[0].price).toFixed(1)}`;
  for (let i = 1; i < points.length; i++) {
    const x = plot.x(points[i].t).toFixed(1);
    d += `H${x}V${plot.y(points[i].price).toFixed(1)}`;
  }
  return d;
}

/** The last price at or before `t` (what the quote was at that moment), else the first. */
export function priceAt(points: SeriesPoint[], t: number): SeriesPoint | null {
  if (!points.length) return null;
  let lo = 0;
  let hi = points.length - 1;
  if (t < points[0].t) return points[0];
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (points[mid].t <= t) lo = mid;
    else hi = mid - 1;
  }
  return points[lo];
}

/** Time-axis label: clock time inside a day and a half, the date beyond. */
export function fmtTick(t: number, spanMs: number): string {
  const d = new Date(t);
  return spanMs <= 36 * 3_600_000
    ? d.toLocaleTimeString(undefined, { hour: "numeric" })
    : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Pins closer than `minGap` pixels share one badge ("3–4"), so none hide behind another. Input in
 * time order; each group sits at its first pin.
 */
export function clusterPins<T extends { t: number; n: number }>(
  pins: T[],
  x: (t: number) => number,
  minGap: number,
): { x: number; label: string; pins: T[] }[] {
  const out: { x: number; label: string; pins: T[] }[] = [];
  for (const p of pins) {
    const last = out[out.length - 1];
    if (last && x(p.t) - last.x < minGap) last.pins.push(p);
    else out.push({ x: x(p.t), label: "", pins: [p] });
  }
  for (const g of out) {
    const ns = g.pins.map((p) => p.n);
    g.label = ns.length === 1 ? String(ns[0]) : `${ns[0]}–${ns[ns.length - 1]}`;
  }
  return out;
}
